import * as React from "react";
import {useEffect, useState} from "react";
import {X} from "lucide-react";

// The "Add Tip" step shown before a Card on File payment is charged. Same
// layout and maths as Vino POS's tip modal, in the Vara theme. Card (terminal)
// payments skip it: the customer tips on the terminal and the API records
// that tip from the terminal's reply.
interface TipModalProps {
    isOpen: boolean;
    // What the customer pays before the tip (card fee included for card
    // methods). Tip percentages are taken from this, as in Vino.
    priceCents: number;
    priceLabel: string;
    clientName?: string;
    onClose: () => void;
    onConfirm: (tipCents: number) => void;
}

const tipPresets = [
    {label: "15%", value: 15},
    {label: "18%", value: 18},
    {label: "20%", value: 20},
    {label: "25%", value: 25},
];

const TipModal: React.FC<TipModalProps> = ({isOpen, priceCents, priceLabel, clientName, onClose, onConfirm}) => {
    const [tipAmount, setTipAmount] = useState(0);
    const [tipType, setTipType] = useState<"percentage" | "dollar">("percentage");
    const [customTip, setCustomTip] = useState("");

    const price = priceCents / 100;

    useEffect(() => {
        if (!isOpen) {
            setTipAmount(0);
            setTipType("percentage");
            setCustomTip("");
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const calculateTipAmount = (percent: number) => (price * percent) / 100;

    const handleTipSelect = (percent: number) => {
        setTipAmount(calculateTipAmount(percent));
        setTipType("percentage");
        setCustomTip("");
    };

    const handleCustomTip = () => {
        const value = parseFloat(customTip);
        if (isNaN(value) || value < 0) return;
        if (tipType === "percentage") setTipAmount(calculateTipAmount(value));
        else setTipAmount(value);
    };

    return (
        <div className="absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-black bg-opacity-50">
            <div className="mx-4 max-h-full w-full max-w-md overflow-y-auto rounded-xl bg-default-background shadow-xl">
                <div className="flex items-center justify-between border-b border-neutral-200 p-4">
                    <div>
                        <h3 className="text-lg font-semibold text-neutral-900">Add Tip</h3>
                        <p className="text-sm text-neutral-600">
                            {priceLabel}: ${price.toFixed(2)}
                            {clientName && <span className="ml-2 font-medium text-neutral-900">• {clientName}</span>}
                        </p>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1 transition-colors hover:bg-neutral-100">
                        <X className="h-5 w-5 text-neutral-400"/>
                    </button>
                </div>

                <div className="p-4">
                    {/* Preset Tips */}
                    <div className="mb-6">
                        <h4 className="mb-3 text-sm font-medium text-neutral-900">Quick Tip</h4>
                        <div className="grid grid-cols-4 gap-2">
                            {tipPresets.map((preset) => (
                                <button
                                    key={preset.label}
                                    onClick={() => handleTipSelect(preset.value)}
                                    className={`rounded-lg border p-3 text-center transition-colors ${
                                        Math.abs(tipAmount - calculateTipAmount(preset.value)) < 0.01
                                            ? "border-neutral-900 bg-brand-primary text-neutral-900"
                                            : "border-neutral-200 hover:border-neutral-400 hover:bg-brand-50"
                                    }`}
                                >
                                    <div className="font-medium">{preset.label}</div>
                                    <div className="text-xs text-neutral-600">${calculateTipAmount(preset.value).toFixed(2)}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Custom Tip */}
                    <div className="mb-6">
                        <h4 className="mb-3 text-sm font-medium text-neutral-900">Custom Tip</h4>
                        <div className="mb-3 flex rounded-lg bg-neutral-100 p-1">
                            <button
                                onClick={() => setTipType("percentage")}
                                className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                                    tipType === "percentage" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-600 hover:text-neutral-900"
                                }`}
                            >
                                Percentage (%)
                            </button>
                            <button
                                onClick={() => setTipType("dollar")}
                                className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                                    tipType === "dollar" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-600 hover:text-neutral-900"
                                }`}
                            >
                                Dollar ($)
                            </button>
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="number"
                                value={customTip}
                                onChange={(e) => setCustomTip(e.target.value)}
                                placeholder={tipType === "percentage" ? "0" : "0.00"}
                                min="0"
                                max={tipType === "percentage" ? "100" : undefined}
                                step={tipType === "percentage" ? "1" : "0.01"}
                                className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-primary"
                            />
                            <button
                                onClick={handleCustomTip}
                                disabled={!customTip || parseFloat(customTip) < 0}
                                className="rounded-lg bg-neutral-900 px-4 py-2 text-white transition-colors hover:bg-neutral-700 disabled:cursor-not-allowed disabled:bg-neutral-300"
                            >
                                Apply
                            </button>
                        </div>
                    </div>

                    {/* No Tip */}
                    <div className="mb-6">
                        <button
                            onClick={() => setTipAmount(0)}
                            className={`w-full rounded-lg border p-3 text-center transition-colors ${
                                tipAmount === 0
                                    ? "border-neutral-400 bg-neutral-50 text-neutral-700"
                                    : "border-neutral-200 text-neutral-600 hover:border-neutral-300"
                            }`}
                        >
                            No Tip
                        </button>
                    </div>

                    {/* Total Summary */}
                    <div className="mb-4 rounded-lg bg-neutral-50 p-4">
                        <div className="mb-1 flex justify-between text-sm text-neutral-600">
                            <span>Subtotal:</span>
                            <span>${price.toFixed(2)}</span>
                        </div>
                        <div className="mb-2 flex justify-between text-sm text-neutral-600">
                            <span>Tip:</span>
                            <span>${tipAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between border-t border-neutral-200 pt-2 text-lg font-semibold text-neutral-900">
                            <span>Total:</span>
                            <span>${(price + tipAmount).toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                <div className="border-t border-neutral-200 p-4">
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 rounded-lg bg-neutral-100 px-4 py-2 text-neutral-700 transition-colors hover:bg-neutral-200"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => onConfirm(Math.round(tipAmount * 100))}
                            className="flex-1 rounded-lg bg-neutral-900 px-4 py-2 text-white transition-colors hover:bg-neutral-700"
                        >
                            Process Payment
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TipModal;
